// Etiquetas, tonos y formatos de la pantalla Clientes del Admin VL. Puro.
import type { PaymentKind } from '@/api/services/retail-customer/backoffice';
import { ACCOUNT_ONLY_LABEL } from '@/shared/lib/format/accountOnly';
import type { PillTone } from '@/shared/patterns/Pills';
import type { CustomerStatus } from './customersList';

export const ACCOUNT_ONLY_NOTE = 'Abrió su cuenta PCF y no ha comprado';

export const STATUS_LABEL: Record<CustomerStatus, string> = {
    ACTIVE: 'Activo',
    NEW: 'Nuevo',
    REGULAR: 'Regular',
    DORMANT: 'Dormido',
    ACCOUNT_ONLY: ACCOUNT_ONLY_LABEL,
};

export const STATUS_TONE: Record<CustomerStatus, PillTone> = {
    ACTIVE: 'success',
    NEW: 'info',
    REGULAR: 'neutral',
    DORMANT: 'neutral',
    ACCOUNT_ONLY: 'info',
};

export const STATUS_HINT: Record<CustomerStatus, string> = {
    ACTIVE: 'Compró en los últimos 30 días',
    NEW: 'Una sola compra, en los últimos 30 días',
    REGULAR: 'Su última compra fue hace 31 a 90 días',
    DORMANT: 'Lleva más de 90 días sin comprar',
    ACCOUNT_ONLY: ACCOUNT_ONLY_NOTE,
};

const PAYMENT_LABEL: Record<string, string> = {
    CASH: 'Contado',
    SUREKEEP: 'Crédito SK',
    PAYJOY: 'PayJoy',
    CREDIYA: 'CrediYa',
    LESPAGO: 'LesPago',
    INNOVA: 'Innova',
};

/** «Contado», «Crédito SK» o el nombre de la financiera externa. */
export function paymentLabel(payment: PaymentKind): string {
    return PAYMENT_LABEL[payment] ?? payment;
}

const MXN = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' });
const MXN_WHOLE = new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    maximumFractionDigits: 0,
});

/** `$20,000.00`. Recibe centavos. */
export function money(cents: number): string {
    return MXN.format(cents / 100);
}

/** `$20,000`, para las columnas angostas de la tabla. */
export function moneyWhole(cents: number): string {
    return MXN_WHOLE.format(cents / 100);
}

/** «hoy» o «12d», como la columna «Días s/compra» del mock. */
export function daysLabel(days: number): string {
    return days <= 0 ? 'hoy' : `${String(days)}d`;
}

/** Rojo arriba de 90 días, ámbar arriba de 30, gris en lo demás. */
export function daysToneClass(days: number): string {
    if (days > 90) return 'text-danger';
    if (days > 30) return 'text-warning';
    return 'text-muted-foreground';
}

const SHORT_ID_CHARS = 6;

/** Cola del ULID: el prefijo es la fecha y se repite entre clientes del mismo día. */
export function shortId(id: string): string {
    return id.length <= SHORT_ID_CHARS ? id : `…${id.slice(-SHORT_ID_CHARS)}`;
}

/** Iniciales del nombre: «Sofía Vargas» → «SV». */
export function initialsOf(name: string | null): string {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '?';
}

export const AGGREGATES_NO_SOURCE =
    'Los totales de por vida los contesta el data lake, que todavía no recibe las ventas de retail.';

export const LEVEL_FORBIDDEN =
    'Tu rol todavía no puede leer la cartera SureKeep, de donde sale el nivel.';

export const LEVEL_ERROR =
    'No se pudo leer la cartera SureKeep. Recarga la página para reintentar.';

export const REBUY_RULE_MISSING =
    'Falta definir la regla de negocio de recompra; la píldora se activa cuando exista.';

export const EMAIL_NO_SOURCE =
    'El correo vive en el expediente del cliente, que tu rol todavía no puede leer.';

export const FAVORITE_STORE_NOTE =
    'Tienda donde más ha comprado; si solo tiene cuenta, la tienda donde la abrió.';

export const LEVEL_MISSING = 'SureKeep no tiene nivel para este cliente.';

export const PREPAID_NO_CATEGORY =
    'El catálogo todavía no tiene una categoría de tarjetas o prepago.';
